/**
 * EventDetailBody — everything of one event below the Event Progress rail: Basic Information (with
 * Parent Event and Event Hierarchy), Event Introduction, Attendance List and the Dashboard.
 *
 * Split out of EventDetailPage so the page can show it once per event in its navigator: for the event
 * itself under "Overall", and for each event beneath it, at any depth (ChildEventTab). Each instance
 * loads what is per event — its attendees, its Parent Event — so a nested event's pane is the same view
 * as that event's own page, minus the page's header actions, which stay with the page's event.
 *
 * The Event Hierarchy field shows on Overall only (`showHierarchy`): in a nested event's pane the
 * navigator beside it already draws that event's place in the tree, and a second tree would only
 * repeat it. Its own page still shows it.
 *
 * The page's overlay waits for the first page of attendees only; later pages arrive into the table
 * behind it. Only the page's own view drives that overlay (`pageOverlay`): it masks the whole content
 * column, navigator included, so a nested event's pane doing so would block switching events until its
 * attendees arrive. That pane shows the Attendance List's own loading state instead.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  ColumnLayout,
  Container,
  Header,
  Link,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import { canManageEvents, isBd } from '../../utils/authorization';
import { editHrefFor } from '../../utils/nominationEdit';
import { AttendanceList } from '../../components/AttendanceList';
import { NominationDetail } from '../../components/NominationDetail';
import QuickSightReport from '../../components/QuickSightReport/QuickSightReport';
import {
  EVENT_DETAIL_ACCOUNT,
  EVENT_DETAIL_DASHBOARD_ID,
  EVENT_DETAIL_EVENT_PARAM,
  EVENT_DETAIL_SHEET_ID,
} from '../../config/dashboards';
import { useEventAttendees } from '../../hooks/useEventAttendees';
import { useEventHierarchy, type ParentEventState } from '../../hooks/useEventHierarchy';
import { attendeeCsv } from '../../utils/attendees';
import { csvFilename, downloadCsv, toCsv } from '../../utils/csv';
import type { Event, EventAttendee, Nomination } from '../../client/EventCalendarClient';
import { NONE, formatDateTime, formatRange } from '../../utils/eventDates';
import { subTypeLabel } from '../../utils/eventTaxonomy';
import {
  nominationRequiredLabel,
  nominationWindow,
  targetLines,
} from '../../utils/eventDetailFields';
import { CampaignHierarchyView } from '../../components/CampaignHierarchy';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import { EventDescriptionView } from '../../components/EventDescriptionView';
import { isEmptyDescription } from '../../utils/eventDescription';

/**
 * One labelled field in the Basic Information grid. Labels are English (testing feedback),
 * under the same names the Edit Event form uses, though the Figma's are Chinese.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

/** One line per entry, or a dash when there are none. */
function Lines({ lines }: { lines: readonly string[] }) {
  if (!lines.length) return <>{NONE}</>;
  return (
    <>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </>
  );
}

/** Province + city, joined; either may be missing. */
function placeOf(event: Event): string {
  const parts = [event.province, event.city].filter(Boolean);
  return parts.length ? parts.join(' · ') : NONE;
}

/**
 * The draft/published axis as the Figma draws it: a green tick beside "Published". DRAFT
 * is not in the design (it only ever shows a published event), so it gets the pending
 * treatment — it is a state waiting on an action, not a neutral one.
 */
function statusOf(event: Event) {
  if (event.contentStatus === 'PUBLISHED') {
    return <StatusIndicator type="success">Published</StatusIndicator>;
  }
  if (event.contentStatus === 'DRAFT') {
    return <StatusIndicator type="pending">Draft</StatusIndicator>;
  }
  return <Box color="text-status-inactive">{NONE}</Box>;
}

export interface EventDetailBodyProps {
  event: Event;
  roles: Parameters<typeof canManageEvents>[0];
  login: string;
  /**
   * The event's hierarchy, when the host has already loaded it (the page does, for its tabs); the
   * body loads its own otherwise.
   */
  hierarchy?: ReturnType<typeof useEventHierarchy>;
  /**
   * The parent event, when the host already has it (a child's tab: the page's own event), so the
   * body does not read it again. Only used when `hierarchy` is not given.
   */
  knownParent?: ParentEventState;
  /** Whether the first attendee page holds the page's loading overlay. Default true; false in a child tab. */
  pageOverlay?: boolean;
  /**
   * Whether to show the Event Hierarchy field. Default true; false in a nested event's pane (see the
   * header). When false and `knownParent` is given, no hierarchy read is made at all.
   */
  showHierarchy?: boolean;
}

export default function EventDetailBody({
  event,
  roles,
  login,
  hierarchy: given,
  knownParent,
  pageOverlay = true,
  showHierarchy = true,
}: EventDetailBodyProps) {
  const navigate = useNavigate();
  const eventId = event.eventId;
  // Nothing to read when the host has it, or when only the parent was wanted and the host gave that.
  const own = useEventHierarchy(
    given || (!showHierarchy && knownParent !== undefined) ? undefined : eventId,
    { knownParent },
  );
  const { nodes: hierarchy, eventNames, parentEvent } = given ?? own;

  /**
   * The nomination whose detail modal is open, or null. Plain state, not the URL: only the
   * Nomination List puts the open nomination in its URL (that is the page a link is shared
   * from); here the modal is a quick look that should leave the reader on this page.
   */
  const [viewing, setViewing] = useState<Nomination | null>(null);

  /* One fetch, two cards: every page of ListEventAttendees. The service decides what each role
     sees, so this card inherits that rule rather than inventing one. */
  const {
    attendees,
    loading: loadingAttendees,
    error: attendeesError,
    replaceNomination,
  } = useEventAttendees(eventId);
  const firstPagePending = loadingAttendees && attendees.length === 0;
  useEventHubLoading(pageOverlay && firstPagePending);

  /** Stable across re-renders, so the embed doesn't re-apply the parameter on every one. */
  const dashboardEventKey = useMemo(() => (eventId ? [eventId] : []), [eventId]);

  /**
   * Exports what the card is showing.
   *
   * Built from the same values the table renders rather than from the raw nomination, so the
   * file and the screen cannot disagree — including the four columns with no field behind them
   * yet, which export as empty for the same reason they display as dashes.
   *
   * The rows come from the card, which owns the search box: exporting every attendee here
   * would ignore whatever the reader filtered to.
   */
  function onExportCsv(rowsToExport: readonly EventAttendee[]) {
    // Every Attendance List column, in its order and under its names (attendeeCsv).
    const { headers, rows } = attendeeCsv(rowsToExport);
    downloadCsv(
      csvFilename(`${event.eventName ?? 'event'}-attendance`, 'attendance'),
      toCsv(headers, rows),
    );
  }

  return (
    <>
      <Box padding={{ top: 'l' }}>
        <Container header={<Header variant="h2">Basic Information</Header>}>
          {/* Four columns with dividers, matching the Figma's layout. */}
          <ColumnLayout columns={4} variant="text-grid">
            <Field label="Event Time">
              {formatRange(event.startDate, event.endDate)}
            </Field>
            <Field label="Location">{placeOf(event)}</Field>
            <Field label="Status">{statusOf(event)}</Field>
            <Field label="BU">
              {event.businessUnits?.length ? event.businessUnits.join(', ') : NONE}
            </Field>
            <Field label="Created By">{event.createdBy || NONE}</Field>
            <Field label="Created At">{formatDateTime(event.createdAt)}</Field>
            <Field label="Event Sub Type">{subTypeLabel(event.eventSubType)}</Field>
            <Field label="SFDC Campaign ID">{event.sfdcCampaignId || NONE}</Field>
            <Field label="Parent Event">
              {parentEvent === undefined ? (
                <StatusIndicator type="loading">Loading</StatusIndicator>
              ) : parentEvent === 'error' ? (
                <Box color="text-status-warning">Could not be loaded</Box>
              ) : parentEvent ? (
                <Link
                  href={`/event/events/${parentEvent.eventId}`}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(`/event/events/${parentEvent.eventId}`);
                  }}
                >
                  {parentEvent.eventName || parentEvent.eventId}
                </Link>
              ) : (
                NONE
              )}
            </Field>
            <Field label="Nomination Required">{nominationRequiredLabel(event)}</Field>
            <Field label="Nomination Time">{nominationWindow(event)}</Field>
            <Field label="Target">
              <Lines lines={targetLines(event)} />
            </Field>
          </ColumnLayout>
          {/* Its own full-width row: a tree needs the width a grid cell does not have. */}
          {showHierarchy ? (
            <Box padding={{ top: 'l' }}>
              <Field label="Event Hierarchy">
                {hierarchy === undefined ? (
                  <StatusIndicator type="loading">Loading</StatusIndicator>
                ) : hierarchy === null ? (
                  <Box color="text-status-warning">Could not be loaded</Box>
                ) : hierarchy.length ? (
                  <CampaignHierarchyView
                    eventName={event.eventName}
                    campaignId={event.sfdcCampaignId}
                    nodes={hierarchy}
                    eventNames={eventNames}
                    onOpenEvent={(id) => navigate(`/event/events/${id}`)}
                  />
                ) : (
                  NONE
                )}
              </Field>
            </Box>
          ) : null}
        </Container>
      </Box>

      {/* Not in the Figma: the introduction marketers write on Edit Event. Hidden when there
          is none, rather than a card that only says so. */}
      {!isEmptyDescription(event.description) ? (
        <Box padding={{ top: 'l' }}>
          <Container header={<Header variant="h2">Event Introduction</Header>}>
            <EventDescriptionView html={event.description ?? ''} />
          </Container>
        </Box>
      ) : null}

      {/* Kept apart from the page's own load error: the event is on screen and readable,
          so this is a notice about two cards rather than a failed page. Said out loud
          because both cards' empty states otherwise read as "nobody has nominated yet",
          which is a different and reassuring claim. */}
      {attendeesError ? (
        <Box padding={{ top: 'l' }}>
          <Alert type="warning" header="Could not load this event's attendees">
            {attendeesError} — the Attendance List may be incomplete or
            empty because the list could not be read, not because there are none.
          </Alert>
        </Box>
      ) : null}

      <Box padding={{ top: 'l' }}>
        <AttendanceList
          attendees={attendees}
          loading={firstPagePending}
          loadingMore={loadingAttendees && attendees.length > 0}
          /* A BD's view is scoped by the service (their territories, their own and reports'
             nominations); a marketer or admin sees the whole event. */
          scoped={isBd(roles) && !canManageEvents(roles)}
          onSelectNomination={setViewing}
          login={login}
          onNominationUpdated={replaceNomination}
          eventId={eventId}
          onOpenEvent={(id) => navigate(`/event/events/${id}`)}
          /* Hands off to the review queue rather than deciding here: Approve / Reject
             already live there with the owner gate and the irreversible-decision dialog,
             and a second entry point would be a second chance to disagree. */
          /* An event that takes no nominations has none to review. */
          onReview={
            event?.bdNominationSupported
              ? () => navigate(`/event/nominations?eventId=${eventId}`)
              : undefined
          }
          onExportCsv={onExportCsv}
        />
      </Box>

      {/* As on the Event Lifecycle Data tab: embedded per registered user, as the login Event
          Hub acts as, so Octopus's row-level security sees the right alias. The event comes
          from the URL — the dashboard keys Event Hub events by the same eventId. */}
      {eventId ? (
        <Box padding={{ top: 'l' }}>
          <Container header={<Header variant="h2">Dashboard by BU</Header>}>
            <QuickSightReport
              dashboardId={EVENT_DETAIL_DASHBOARD_ID}
              accountShortName={EVENT_DETAIL_ACCOUNT}
              sheetId={EVENT_DETAIL_SHEET_ID}
              useRegisteredUser
              userAlias={login}
              paramName={EVENT_DETAIL_EVENT_PARAM}
              paramValues={dashboardEventKey}
              height="800"
            />
          </Container>
        </Box>
      ) : null}

      {viewing ? (
        <NominationDetail
          nomination={viewing}
          eventName={event.eventName || eventId || ''}
          onDismiss={() => setViewing(null)}
          editHref={editHrefFor(viewing, event, login, new Date())}
        />
      ) : null}
    </>
  );
}
